import { createContext, useContext, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from './utils/cn';

interface TabsCtx {
  active: string;
  setActive: (v: string) => void;
  baseId: string;
}

const Ctx = createContext<TabsCtx | null>(null);

function useTabs(): TabsCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('Tabs.* must be inside <Tabs>');
  return c;
}

export interface TabsProps {
  defaultValue: string;
  value?: string;
  onValueChange?: (v: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue);
  const baseId = useId();
  const active = value ?? internal;
  const ctx = useMemo<TabsCtx>(
    () => ({
      active,
      setActive: (v) => {
        if (value === undefined) setInternal(v);
        onValueChange?.(v);
      },
      baseId,
    }),
    [active, baseId, onValueChange, value],
  );
  return (
    <Ctx.Provider value={ctx}>
      <div className={className}>{children}</div>
    </Ctx.Provider>
  );
}

export interface TabsListProps {
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}

export function TabsList({ children, className, ariaLabel }: TabsListProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex items-center gap-1 border-b border-border overflow-x-auto',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

export function TabsTrigger({ value, children, disabled, className }: TabsTriggerProps) {
  const { active, setActive, baseId } = useTabs();
  const selected = active === value;
  return (
    <button
      type="button"
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-controls={`${baseId}-panel-${value}`}
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={() => setActive(value)}
      className={cn(
        'px-3 sm:px-4 py-2.5 -mb-px border-b-2 text-sm font-medium whitespace-nowrap',
        'transition-colors',
        selected
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-fg-muted hover:text-fg hover:border-border-strong',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      {children}
    </button>
  );
}

export interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { active, baseId } = useTabs();
  if (active !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      className={cn('pt-4', className)}
    >
      {children}
    </div>
  );
}
